import { FilterControl } from '../common';

export interface ChartDropdownProps<K extends string> {
  // The prefix before the current value ("Chart type" / "Group").
  label: string;
  options: readonly { key: K; label: string; icon?: string }[];
  value: K;
  onChange: (k: K) => void;
  // Kept on screen but inert when the current view has no use for it. Unmounting
  // it reflows the toolbar under the cursor that just made the selection.
  disabled?: boolean;
  disabledHint?: string;
}

// The chart-toolbar selector IS the global filter pill (FilterControl) — structurally
// identical to every other filter (leading type icon + "Label:" + a value chip with
// the option's own glyph), but as a REQUIRED single pick: a chart always has a
// type/group, so it can't clear to "All" (clearable={false}). The leading 'bar' icon
// is the chart-subject glyph, matching how Status/Role/Staff each lead with theirs.
// View-state only.
export function ChartDropdown<K extends string>({ label, options, value, onChange, disabled, disabledHint }: ChartDropdownProps<K>) {
  // A required selector always shows a value — resolve an unknown one to the first
  // option (never "All") so the pill is never empty.
  const current = options.find((o) => o.key === value) ?? options[0];
  return (
    <FilterControl
      label={label}
      icon="bar"
      mode="single"
      clearable={false}
      disabled={disabled}
      disabledHint={disabledHint}
      options={options.map((o) => ({ key: o.key, label: o.label, icon: o.icon }))}
      selected={current ? [current.key] : []}
      onChange={(keys) => {
        if (keys[0]) onChange(keys[0] as K);
      }}
    />
  );
}
